import React from 'react';
import { Box, Stack, ButtonBase, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useSettingsContext } from '../../../../setting/SettingsContext';
import SectionLabel from './SectionLabel';

const OPTIONS = [
    { value: 'integrate', en: 'Integrate', ar: 'مدمج' },
    { value: 'apparent', en: 'Apparent', ar: 'بسيط' },
];

/**
 * Tiny preview of how the sidebar will look in each Nav color mode.
 * Renders a 2-rect mock: left rect = sidebar, right rect = main content.
 */
const NavPreview = ({ variant, primary, divider, surface }) => {
    const isApparent = variant === 'apparent';
    return (
        <Box
            sx={{
                width: '100%',
                height: 44,
                display: 'flex',
                gap: 0.5,
                borderRadius: 1,
                overflow: 'hidden',
                border: `1px solid ${divider}`,
                bgcolor: surface,
            }}
        >
            <Box
                sx={{
                    width: 14,
                    bgcolor: isApparent ? surface : primary,
                    borderInlineEnd: isApparent ? `1px solid ${divider}` : 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    p: 0.5,
                }}
            >
                <Box sx={{ width: 6, height: 3, borderRadius: 0.5, bgcolor: isApparent ? primary : '#FFFFFF' }} />
                <Box sx={{ width: 8, height: 2, borderRadius: 0.5, bgcolor: isApparent ? divider : 'rgba(255,255,255,0.6)' }} />
                <Box sx={{ width: 8, height: 2, borderRadius: 0.5, bgcolor: isApparent ? divider : 'rgba(255,255,255,0.6)' }} />
            </Box>
            <Box sx={{ flex: 1, p: 0.5 }}>
                <Box sx={{ width: '60%', height: 3, borderRadius: 0.5, bgcolor: divider, mb: 0.5 }} />
                <Box sx={{ width: '40%', height: 2, borderRadius: 0.5, bgcolor: divider }} />
            </Box>
        </Box>
    );
};

/**
 * Sidebar color picker — Integrate (vivid primary background) vs
 * Apparent (neutral surface). Drives themeNavColor on SettingsContext;
 * SideBar reads it.
 */
const NavColorOptions = () => {
    const theme = useTheme();
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { themeNavColor, onChangeNavColor } = useSettingsContext();

    const previewSurface = theme.palette.background.paper || '#FFFFFF';

    return (
        <Box>
            <SectionLabel>{isArabic ? 'لون القائمة' : 'Nav color'}</SectionLabel>
            <Stack direction="row" spacing={1.25}>
                {OPTIONS.map(({ value, en, ar }) => {
                    const selected = themeNavColor === value;
                    return (
                        <ButtonBase
                            key={value}
                            onClick={() => onChangeNavColor(value)}
                            sx={{
                                flex: 1,
                                flexDirection: 'column',
                                gap: 0.75,
                                p: 1.25,
                                borderRadius: 2,
                                border: `1.5px solid ${
                                    selected ? theme.palette.primary.main : theme.palette.divider
                                }`,
                                bgcolor: selected
                                    ? `${theme.palette.primary.main}12`
                                    : 'transparent',
                                color: selected ? theme.palette.primary.main : 'text.primary',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    bgcolor: `${theme.palette.primary.main}08`,
                                },
                            }}
                        >
                            <NavPreview
                                variant={value}
                                primary={theme.palette.primary.main}
                                divider={theme.palette.divider}
                                surface={previewSurface}
                            />
                            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                                {isArabic ? ar : en}
                            </Typography>
                        </ButtonBase>
                    );
                })}
            </Stack>
        </Box>
    );
};

export default NavColorOptions;
